'use client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { TRACE_EVENT_ID, TRACE_EVENT_NAME } from '@/app/constants';
import { useFetch, useRecreate } from '@/app/hooks';
import type { CommunityEnum, NewShowCaseList, ShowCaseList, TaskInfo, UpdateInteractionStatusFn } from '@/app/types';
import { getCurrentItem, getItemType, isExtendTask, isImageTask, isShowCaseType, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { inspirationsGetInspirationDetail } from '../apis';
import type { MediaAssetItem, ShortFilmItem } from '../types';

export const useFeedCard = ({
  handleUnVote,
  handleDetail,
  handleVote,
  interactionPosition,
  openRecreateInNewTab,
  onRecreateStart,
}: {
  handleDetail: (id: string, traceId?: string) => void;
  handleVote?: UpdateInteractionStatusFn;
  handleUnVote?: UpdateInteractionStatusFn;
  interactionPosition?: string;
  openRecreateInNewTab?: boolean;
  onRecreateStart?: () => void;
}) => {
  const t = useTranslations();
  const pathname = usePathname();
  const [reCreateTraceId, setReCreateTraceId] = useState<string>('');
  const { handleRecreate } = useRecreate(); //做同款

  const { run: getRecreateDetail } = useFetch(
    // 都用inspiration
    (id: string) =>
      inspirationsGetInspirationDetail({
        path: {
          id,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error, response }, params: [string, val?: string]) => {
        const [_, traceId] = params;
        if (!traceId) {
          setReCreateTraceId(response.headers.get('x-md-trace-id') ?? '');
        }

        if (error) {
          toast({
            message: error.message,
            type: 'error',
          });
        }
        if (data) {
          onRecreateStart?.();
          handleRecreate({
            creationId: data.task?.creations[0]?.id || '',
            input: data.task?.input || { enhance: true },
            inspirationId: data.inspiration?.id || '',
            interactionPosition: interactionPosition,
            openNewTab: openRecreateInNewTab ?? true,
            settings: data.task?.settings || {},
            taskId: data.task?.id || '',
            taskType: data.task?.type || 'unspecified',
            templateScene: data.task?.scene,
            traceId: traceId || reCreateTraceId || '',
          });
        }
      },
    },
  );

  const getExtraInfo = (item: ShowCaseList[number] | undefined, traceId: string | undefined) => {
    switch (item?.type) {
      case 'scene':
      case 'scene2':
        return {
          actions: [
            {
              key: 'template',
              title: t('home.explore.promptButton'),
              onClick: () => getRecreateDetail(item?.id || '', traceId),
            },
          ],
        };
      case 'tutorial':
        return {
          detail: item?.detail,
          title: item?.title,
          ...(item?.source === 'official' && {
            avatar: undefined,
            nickName: undefined,
            medals: [],
          }),
        };
      case 'short_film':
        return {
          detail: item.detail,
          info:
            item?.phase && item.phase !== 'unspecified'
              ? item?.phase === 'finished'
                ? t('shortFilm.episods', { n: item.latest_episode_num || 0 })
                : t('shortFilm.updateTo', { n: item?.latest_episode_num || 0 })
              : undefined,
          title: item.title,
        };
      case 'auto2video':
      case 'remix2video':
      case 'video_remix':
      case 'music_video':
      case 'lip_sync':
        return {
          detail: item?.detail,
        };
      default:
        return (item?.type && isExtendTask(item.type as TaskInfo['type'])) ||
          (item?.creation?.origin_task_type && isExtendTask(item.creation?.origin_task_type as TaskInfo['type']))
          ? {
              actions: [],
              detail: item?.detail || t('task.prompt.placeholder'),
            }
          : {
              actions: [
                ...(isImageTask({ type: item?.type as TaskInfo['type'] })
                  ? [
                      {
                        key: 'apply',
                        title: t('creation.action.apply'),
                      },
                    ]
                  : []),
                {
                  key: 'recreate',
                  title: t('home.explore.promptButton'),
                  onClick: () => getRecreateDetail(item?.id || '', traceId),
                },
              ],
              detail: item?.detail,
            };
    }
  };

  const getInfoWithType = ({ item }: { item: NewShowCaseList[number] }) => {
    switch (item.type) {
      case 'image_asset':
        return getExtraInfo(item.image_asset, item.traceId);
      case 'media_asset':
        return getExtraInfo(item.media_asset, item.traceId);
      case 'short_film':
        return getExtraInfo(item.short_film, item.traceId);
      case 'tutorial':
        return getExtraInfo(item.tutorial, item.traceId);
      case 'mini_video':
        return getExtraInfo(item.mini_video, item.traceId);
      default:
        return getExtraInfo(item.media_asset, item.traceId);
    }
  };

  const getCommonInfo = ({
    item,
    type,
    traceId,
    hideUser,
  }: {
    item: ShowCaseList[number] | MediaAssetItem | ShortFilmItem | undefined;
    traceId: string;
    type: CommunityEnum;
    hideUser?: boolean;
  }) => {
    const commonParams = {
      avatar: hideUser ? undefined : item?.creator?.avatar,
      medals: hideUser
        ? []
        : item?.creator?.user_medal
          ? [
              {
                user_medal_type: item.creator.user_medal,
                is_active: true,
              },
            ]
          : [],
      eventName: type ? TRACE_EVENT_NAME[type] : '',
      eventProperties: {
        [TRACE_EVENT_ID[type]]: item?.id,
        trace_id: traceId,
      } as Record<string, string>,
      handleDetail: () => {
        handleDetail?.(item?.id || '', traceId);
      },
      handleUnVote: () => {
        handleUnVote?.({
          id: item?.id || '',
          obj_type: type,
          traceId: traceId,
        });
      },
      handleVote: () => {
        handleVote?.({
          id: item?.id || '',
          obj_type: type,
          traceId: traceId,
        });
      },
      nickName: hideUser ? undefined : item?.creator?.nick_name,
      onPlayStart: () => {
        if (type) {
          reportEvent(TRACE_EVENT_NAME[type], {
            [TRACE_EVENT_ID[type]]: item?.id || '',
            interaction_position: interactionPosition || pathname,
            interaction_type: 'hoverplay',
            trace_id: traceId,
            content_type: 'video',
          });
        }
      },
    };
    return commonParams;
  };

  const getCardInfo = ({
    item,
    hideUser,
  }: {
    item: NewShowCaseList[number] | ShowCaseList[number];
    hideUser?: boolean;
  }) => {
    if (isShowCaseType(item)) {
      return {
        ...getCommonInfo({
          item: item,
          traceId: item.traceId || '',
          type: getItemType(item),
        }),
        ...getExtraInfo(item, item.traceId),
      };
    }

    const currentItem = getCurrentItem(item);
    const currentType = getItemType(currentItem);

    return {
      ...getCommonInfo({
        hideUser: hideUser,
        item: currentItem,
        traceId: item.traceId || '',
        type: currentType,
      }),
      ...getInfoWithType({ item }),
    };
  };

  return {
    getCardInfo,
  };
};
